import React, { useState, useCallback, useMemo, useEffect } from 'react';
import GoogleMapReact from 'google-map-react';
import useSupercluster from 'use-supercluster';
import {
  useRouter,
} from "/lib/component/util";
import "./mapStyles.css";

// eslint-disable-next-line react/prop-types
const Marker = React.memo(({ children }) => children);
Marker.displayName = 'Marker';

// eslint-disable-next-line react/prop-types
const SingleMarker = React.memo(() => (
  <div 
    role="button"
    tabIndex={0} 
    style={{
      height: '10px',
      width: '10px',
      backgroundColor: '#9C2948',
      borderRadius: '50%',
    }}
  />
));
SingleMarker.displayName = 'SingleMarker';

const darkTheme = [
  { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
  {
      featureType: 'administrative.locality',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#d59563' }]
  },
  {
      featureType: 'poi',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#d59563' }]
  },
  {
      featureType: 'poi.park',
      elementType: 'geometry',
      stylers: [{ color: '#263c3f' }]
  },
  {
      featureType: 'poi.park',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#6b9a76' }]
  },
  {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [{ color: '#38414e' }]
  },
  {
      featureType: 'road',
      elementType: 'geometry.stroke',
      stylers: [{ color: '#212a37' }]
  },
  {
      featureType: 'road',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#9ca5b3' }]
  },
  {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [{ color: '#746855' }]
  },
  {
      featureType: 'road.highway',
      elementType: 'geometry.stroke',
      stylers: [{ color: '#1f2835' }]
  },
  {
      featureType: 'road.highway',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#f3d19c' }]
  },
  {
      featureType: 'transit',
      elementType: 'geometry',
      stylers: [{ color: '#2f3948' }]
  },
  {
      featureType: 'transit.station',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#d59563' }]
  },
  {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [{ color: '#17263c' }]
  },
  {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#515c6d' }]
  },
  {
      featureType: 'water',
      elementType: 'labels.text.stroke',
      stylers: [{ color: '#17263c' }]
  }
];

// eslint-disable-next-line react/prop-types
const MapChart = ({ points }) => {
  const mapRef = React.useRef();
  const router = useRouter();
  const [bounds, setBounds] = useState(null);
  const [zoom, setZoom] = useState(4);
  const [center, setCenter] = useState({ lat: 39.5, lng: -98.35 });
  const [isLoading, setIsLoading] = useState(true);
  const [loadedClusters, setLoadedClusters] = useState([]);

  const filteredPoints = useMemo(() => {
    return points.filter(point => {
      const [longitude, latitude] = point.geometry.coordinates;
      return !(latitude === 0 && longitude === 0);
    });
  }, [points]);

  const { clusters, supercluster } = useSupercluster({
    points: filteredPoints,
    bounds,
    zoom,
    options: { radius: 75, maxZoom: 20 }
  });
  
  const defaultProps = useMemo(() => ({
    center: center,
    zoom: 4,
  }), [center]);

  useEffect(() => {
    if (clusters.length > 0) {
      setIsLoading(false);
      setLoadedClusters(clusters);
    }
  }, [clusters]);

  useEffect(() => {
    if (filteredPoints && filteredPoints.length > 0) {
      const firstPoint = filteredPoints[0].geometry.coordinates;
      setCenter({ lat: firstPoint[1], lng: firstPoint[0] });
    }
  }, [filteredPoints]);

  const redirectToEntity = useCallback((namespace, name) => {
    router.history.push(`/n/${namespace}/entities/${name}`);
  }, [router.history]);

  const handleClusterClick = useCallback((clusterId, latitude, longitude) => {
    const expansionZoom = Math.min(
      supercluster.getClusterExpansionZoom(clusterId),
      20
    );
    mapRef.current.setZoom(expansionZoom);
    mapRef.current.panTo({ lat: latitude, lng: longitude });
  }, [supercluster]);

  const renderCluster = useCallback(({ geometry, properties, id }) => {
    const [longitude, latitude] = geometry.coordinates;
    const { cluster: isCluster, point_count: pointCount } = properties;

    if (isCluster) {
      return (
        <Marker key={`cluster-${id}`} lat={latitude} lng={longitude}>
          <div
            role="button"
            tabIndex={0}
            className="cluster-marker"
            style={{
              width: `${10 + (pointCount / filteredPoints.length) * 20}px`,
              height: `${10 + (pointCount / filteredPoints.length) * 20}px`
            }}
            onClick={() => handleClusterClick(id, latitude, longitude)}
            onKeyPress={(event) => {
              if (event.key === "Enter") {
                handleClusterClick(id, latitude, longitude);
              }
            }}
          >
            {pointCount}
          </div>
        </Marker>
      );
    }

    return (
      <Marker key={`terminal-${properties.name}`} lat={latitude} lng={longitude}>
        <button className="terminal-marker" onClick={() => redirectToEntity(properties.namespace, properties.name)}>
          <SingleMarker />
        </button>
      </Marker>
    );
  }, [handleClusterClick, redirectToEntity, filteredPoints.length]);

  return (
    <div style={{ height: '425px', width: '100%' }}>
      <GoogleMapReact
        bootstrapURLKeys={{ key: 'AIzaSyAhItMVXYG0TYDFCo1Lf3kDtxRmiZRJrWM' }}
        defaultCenter={defaultProps.center}
        defaultZoom={defaultProps.zoom}
        center={center}
        options={{ styles: darkTheme }}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map }) => {
          mapRef.current = map;
        }}
        onChange={({ zoom, bounds }) => {
          setZoom(zoom);
          setBounds([
            bounds.nw.lng,
            bounds.se.lat,
            bounds.se.lng,
            bounds.nw.lat
          ]);
        }}
      >
        {!isLoading && loadedClusters.map(renderCluster)}
      </GoogleMapReact>
      {isLoading && <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}></div>}
    </div>
  );
};

export default React.memo(MapChart);